import { Box, Button, Grid, Stack } from '@mui/material';
import { useState } from 'react';
import { useForm, useFormContext, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { postSelection } from 'src/shared/services/selectionService';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { MemberSelectionRequest } from '../models/memberSelectionRequest';

type FormValues = {
  selectionName: string;
  selectionInfo: string;
};

type SaveAdvancedSearchSelectionProps = {
  modalOpen: boolean;
  setModalOpen: (closeModel: boolean) => void;
  setUpdateSelectionOptions: (updateOptions: boolean) => void;
};

export const SaveAdvancedSearchSelection = (props: SaveAdvancedSearchSelectionProps) => {
  const { modalOpen, setModalOpen, setUpdateSelectionOptions } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { getValues } = useFormContext();

  const minimumCharRequired = 5;
  const maximumCharRequired = 50;
  const minCharRequiredMessage = t('common:validationMessages.minLength', { count: minimumCharRequired });
  const maxCharRequiredMessage = t('common:validationMessages.maxLength', { count: maximumCharRequired });

  const schema = yup
    .object({
      selectionName: yup.string().min(5, minCharRequiredMessage).max(50, maxCharRequiredMessage).required(),
      selectionInfo: yup.string().optional().nullable(),
    })
    .required();

  const formMethods = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      selectionName: '',
      selectionInfo: '',
    },
  });
  const {
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = formMethods;

  const closeModal = () => {
    reset();
    setModalOpen(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function removeEmptyArraysAndNulls(obj: Record<string, any>) {
    return Object.fromEntries(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      Object.entries(obj).filter(([_, value]) => value !== undefined && value !== null && !(Array.isArray(value) && value.length === 0)),
    );
  }

  const onHandleSave = async (data: FormValues) => {
    setIsLoading(true);
    const advancedSearchFormValues = getValues();

    const converted = {
      ...advancedSearchFormValues,
      dueDateEnd: advancedSearchFormValues.dueDateEnd ? DateHelper.getDateTime(advancedSearchFormValues.dueDateEnd) : '',
      dueDateStart: advancedSearchFormValues.dueDateStart ? DateHelper.getDateTime(advancedSearchFormValues.dueDateStart) : '',

      admDateEnd: advancedSearchFormValues.admDateEnd ? DateHelper.getDateTime(advancedSearchFormValues.admDateEnd) : '',
      admDateStart: advancedSearchFormValues.admDateStart ? DateHelper.getDateTime(advancedSearchFormValues.admDateStart) : '',

      payDateEnd: advancedSearchFormValues.payDateEnd ? DateHelper.getDateTime(advancedSearchFormValues.payDateEnd) : '',
      payDateStart: advancedSearchFormValues.payDateStart ? DateHelper.getDateTime(advancedSearchFormValues.payDateStart) : '',

      resDateStart: advancedSearchFormValues.resDateStart ? DateHelper.getDateTime(advancedSearchFormValues.resDateStart) : '',
      resDateEnd: advancedSearchFormValues.resDateEnd ? DateHelper.getDateTime(advancedSearchFormValues.resDateEnd) : '',

      birthDateStart: advancedSearchFormValues.birthDateStart ? DateHelper.getDateTime(advancedSearchFormValues.birthDateStart) : '',
      birthDateEnd: advancedSearchFormValues.birthDateEnd ? DateHelper.getDateTime(advancedSearchFormValues.birthDateEnd) : '',
    };

    const filterFormValues = removeEmptyArraysAndNulls(converted);

    const dataToPost: MemberSelectionRequest = {
      mailAddress: '',
      allowedSenders: '',
      emailProfilesMark: '',
      emailProfilesThanxURL: '',
      selectionName: data.selectionName,
      selectionInfo: data.selectionInfo,
      json: filterFormValues,
    };

    try {
      await postSelection(dataToPost);
      if (isMounted()) {
        toast.success(<>{t('member:memberAdvancedSearch.savedSelection')}</>);
        setUpdateSelectionOptions(true);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setIsLoading(false);
      closeModal();
    }
  };

  const isButtonDisabled = isLoading || !!errors.selectionName || !watch('selectionName');

  return (
    <GenericDialog
      open={modalOpen}
      maxWidth='sm'
      onClose={() => closeModal()}
      onSave={handleSubmit(onHandleSave)}
      dialogTitle={t('member:memberAdvancedSearch.saveSelection')}
      dialogContent={
        <Box>
          <FormProvider {...formMethods}>
            <form id='save-selection-form' onSubmit={handleSubmit(onHandleSave)}>
              <GridWrapper>
                <Grid item xs={12} sx={{ pb: { xs: 2 } }}>
                  <ControlledTextField name='selectionName' label={t('member:memberAdvancedSearch.name')} required autoFocus />
                </Grid>
                <Grid item xs={12} sx={{ pb: { xs: 2 } }}>
                  <ControlledTextField multiline minRows={5} name='selectionInfo' label={t('member:selection.description')} />
                </Grid>
              </GridWrapper>
            </form>
          </FormProvider>
        </Box>
      }
      dialogActions={
        <Stack spacing={1} direction='row'>
          <Button size='large' variant='outlined' color='primary' onClick={() => closeModal()}>
            {t('common:button.cancel')}
          </Button>{' '}
          <Button
            form='save-selection-form'
            size='large'
            disabled={isButtonDisabled}
            disableElevation
            variant='contained'
            color='primary'
            type='submit'
          >
            {t('common:button.save')}
          </Button>
        </Stack>
      }
    />
  );
};
